/**
 * @file useConsentTextsOverview.ts
 * @description Данные экрана-списка «Согласие на cookie» (phase19.16, Р3г;
 * uiux-r3g-consent-texts §2): по каждому виду — действующая редакция и
 * история. Список и текущая — с сервера (один источник правды), после
 * публикации экран перечитывает их заново, а не правит руками.
 * Состояние — с ключом запроса (`{ key, data }`, ревью М1).
 */

import type { ConsentTextKind } from "@chaiberi/shared";
import { useCallback, useEffect, useState } from "react";

import { consentTextsApi, type ConsentTextRevision, type ConsentTextRevisionListItem } from "../../lib/api-consent-texts";

export interface ConsentKindOverview {
  /** Действующая редакция; null — редакций нет (сид не прошёл). */
  current: ConsentTextRevision | null;
  history: ConsentTextRevisionListItem[];
}

export interface ConsentTextsOverviewState {
  loading: boolean;
  /** Текст ошибки загрузки или null. */
  error: string | null;
  data: Record<ConsentTextKind, ConsentKindOverview> | null;
  reload: () => void;
}

type Overview = Record<ConsentTextKind, ConsentKindOverview>;

const LOAD_ERROR = "Не удалось загрузить тексты. Сайты магазинов работают со своими опубликованными текстами.";

async function loadKind(kind: ConsentTextKind): Promise<ConsentKindOverview> {
  const history = await consentTextsApi.list(kind);
  // Пустая история — текущей нет: не спрашиваем, чтобы не ловить 404.
  if (history.length === 0) return { current: null, history };
  return { current: await consentTextsApi.current(kind), history };
}

export function useConsentTextsOverview(): ConsentTextsOverviewState {
  const [nonce, setNonce] = useState(0);
  // Ответ хранится с ключом запроса (ревью М1): «загрузка» — пока ответа на этот запрос нет.
  const [loaded, setLoaded] = useState<{ key: number; data: Overview | null; error: string | null } | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([loadKind("BANNER"), loadKind("NOTICE_RISK")])
      .then(([banner, risk]) => ({ data: { BANNER: banner, NOTICE_RISK: risk }, error: null }))
      .catch(() => ({ data: null, error: LOAD_ERROR }))
      .then((result) => {
        // Сбой повтора не прячет прежние данные.
        if (alive) setLoaded((prev) => ({ key: nonce, data: result.data ?? prev?.data ?? null, error: result.error }));
      });
    return () => {
      alive = false;
    };
  }, [nonce]);

  const loading = loaded?.key !== nonce;
  const data = loaded?.data ?? null;
  const error = loading ? null : (loaded?.error ?? null);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { loading, error, data, reload };
}
